import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { Button } from "./ui/button";
import PushNotificationManager from "./PushNotificationManager";
import {
  Calendar,
  Home,
  User,
  LogOut,
  Settings,
  Users,
  Building,
  Palette,
  BookOpen,
  ClipboardList,
  CalendarDays,
  FileText,
  UserCheck,
  Briefcase,
  Inbox,
  ClipboardCheck,
  Film,
  Mail,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Brain,
  Database,
  MessageCircle,
  ScanLine,
  Heart,
  Sparkles,
  Leaf,
  Hotel,
  Globe,
  Target,
  Megaphone,
  GraduationCap,
  Shield,
  BookMarked,
  MapPin,
  Microscope,
  ExternalLink,
  Rss,
  Package,
} from "lucide-react";

const BASE_URL = window.location.origin;

// Helper per costruire URL completo per le immagini
const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BASE_URL}${url}`;
};

export const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { settings } = useSiteSettings();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const isActive = (path) => location.pathname === path;

  // Verifica se è operatore fiera (ruolo limitato)
  const isOperatoreFiera = user?.role === "operatore_fiera";
  
  // Link per operatore fiera (molto limitati)
  const operatoreFieraLinks = [
    { path: "/", label: "Home", icon: Home },
    { path: "/admin/contatti-fiera", label: "Acquisizione Contatti", icon: ScanLine },
  ];

  // Link per POTENZIALE CLIENTE - SOLO BENESSERE
  const potenzialeClienteLinks = [
    { path: "/benessere", label: "La Scienza del Benessere", icon: Sparkles },
    { path: "/benessere/articoli", label: "Articoli Scientifici", icon: BookOpen },
    { path: "/benessere/tecniche", label: "Tecniche di Benessere", icon: Leaf },
  ];

  // Link per CLIENTE ACCADEMIA ODR - solo Biblioteca + Scienza Benessere + Test
  const clienteAccademiaLinks = [
    { path: "/biblioteca", label: "Home Accademia", icon: Home },
    { path: "/quiz", label: "Test di Comprensione", icon: ClipboardCheck },
    { path: "/benessere", label: "La Scienza del Benessere", icon: Sparkles },
    { path: "/benessere/articoli", label: "Articoli Scientifici", icon: BookOpen },
  ];

  // Link per CLIENTI (estetisti/parrucchieri) - TUTTO
  const userLinks = [
    { path: "/", label: "Sessioni", icon: CalendarDays },
    { path: "/calendario", label: "Il Mio Calendario", icon: Calendar },
    { path: "/materiali", label: "I Miei Materiali", icon: FileText },
    { path: "/biblioteca", label: "Biblioteca Corsi", icon: Film },
    { path: "/quiz", label: "Test di Valutazione", icon: ClipboardCheck },
  ];

  // Link BENESSERE per tutti gli utenti registrati (escluso potenziale_cliente che li ha già)
  const publicLinks = [
    { path: "/benessere", label: "La Scienza del Benessere", icon: Sparkles },
    { path: "/benessere/articoli", label: "Articoli Scientifici", icon: BookOpen },
    { path: "/benessere/tecniche", label: "Tecniche di Benessere", icon: Leaf },
  ];

  // Link admin principali - RAGGRUPPATI PER SEZIONE
  const adminGroups = [
    {
      id: "crm",
      label: "CRM & Vendite",
      icon: Target,
      links: [
        { path: "/admin/crm", label: "CRM ITA318", icon: Briefcase },
        { path: "/admin/crm/clienti", label: "Clienti ITA318", icon: UserCheck },
        { path: "/admin/crm/ordini", label: "Ordini in attesa", icon: Inbox },
        { path: "/admin/potenziali-clienti", label: "Potenziali Clienti", icon: UserCheck },
        { path: "/admin/contatti-fiera", label: "Contatti Fiera", icon: ScanLine },
        { path: "/admin/hotel-spa", label: "Hotel & Luxury SPA", icon: Hotel },
        { path: "/admin/marketing", label: "Database Marketing", icon: Database },
        { path: "/admin/referenti", label: "Distributori/Agenti", icon: UserCheck },
      ],
    },
    {
      id: "network",
      label: "Network & Mappa",
      icon: Globe,
      links: [
        { path: "/admin/network-map", label: "ODR Global Network", icon: MapPin },
        { path: "/admin/magazzino", label: "Magazzino ODR", icon: Package },
      ],
    },
    {
      id: "marketing",
      label: "Marketing & Comunicazione",
      icon: Megaphone,
      links: [
        { path: "/admin/comunicazioni", label: "Comunicazioni", icon: Mail },
        { path: "/admin/newsletter", label: "Newsletter", icon: Mail },
        { path: "/admin/whatsapp", label: "Automazione WhatsApp", icon: MessageCircle },
        { path: "/admin/telegram", label: "Telegram Bot", icon: MessageCircle },
        { path: "/admin/ai-engine", label: "AI Content Engine", icon: Brain },
        { path: "/admin/mammouth-ai", label: "Mammouth AI", icon: Brain },
        { path: "/admin/solea", label: "SOLEA Knowledge Base", icon: Brain },
        { path: "/admin/video-generator", label: "Video Generator PRO", icon: Film },
        { path: "/admin/skin-analysis", label: "Analisi Pelle & Capelli", icon: Microscope },
        { path: "/admin/blog-wp", label: "Blog WordPress", icon: Rss },
      ],
    },
    {
      id: "stories",
      label: "ODR Stories",
      icon: BookMarked,
      links: [
        { path: "/admin/stories", label: "ODR Stories Landing", icon: BookMarked },
      ],
    },
    {
      id: "formazione",
      label: "Formazione & Didattica",
      icon: GraduationCap,
      links: [
        { path: "/admin/accademia", label: "Accademia · Approvazioni", icon: GraduationCap },
        { path: "/admin/sessioni", label: "Gestione Sessioni", icon: CalendarDays },
        { path: "/admin/iscrizioni", label: "Iscrizioni", icon: ClipboardList },
        { path: "/admin/tecniche-benessere", label: "Gestione Tecniche", icon: Leaf },
      ],
      subGroups: [
        {
          id: "materiali",
          label: "Materiale Didattico",
          icon: FolderOpen,
          links: [
            { path: "/admin/quiz-risposte", label: "Risposte Test", icon: ClipboardCheck },
            { path: "/admin/materiali", label: "Materiali Didattici", icon: FileText },
            { path: "/materiali-docente", label: "Video Corsi Docente", icon: Film },
            { path: "/biblioteca", label: "Biblioteca Clienti", icon: BookOpen },
          ],
        },
      ],
    },
    {
      id: "sistema",
      label: "Amministrazione",
      icon: Shield,
      links: [
        { path: "/admin/utenti", label: "Gestione Utenti", icon: Users },
        { path: "/admin/docenti", label: "Docenti", icon: User },
        { path: "/admin/associazioni", label: "Associazioni", icon: Building },
        { path: "/admin/documenti-contabili", label: "Documenti Contabili", icon: FileText },
        { path: "/admin/personalizzazione", label: "Personalizzazione", icon: Palette },
      ],
    },
  ];

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // Auto-expand group containing active route
  useEffect(() => {
    for (const group of adminGroups) {
      const allLinks = [
        ...group.links,
        ...(group.subGroups || []).flatMap((sg) => sg.links),
      ];
      if (allLinks.some((l) => isActive(l.path))) {
        setOpenGroups((prev) => ({ ...prev, [group.id]: true }));
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Home path dinamico in base al ruolo
  const homePath = user?.role === "cliente_accademia" ? "/biblioteca" :
                   user?.role === "potenziale_cliente" ? "/benessere" :
                   "/";

  return (
    <>
      {/* Mobile Header Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-border/50 shadow-sm z-[100] flex items-center justify-between px-4">
        <Link to={homePath} className="flex items-center gap-2" data-testid="mobile-home-link">
          {settings?.logo_url ? (
            <img 
              src={getFullUrl(settings.logo_url)} 
              alt={settings?.site_name || "ODR Academy"} 
              className="h-10 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className="w-10 h-10 rounded-full bg-primary items-center justify-center"
            style={{ display: settings?.logo_url ? 'none' : 'flex' }}
          >
            <span className="text-white font-serif font-bold text-lg">O</span>
          </div>
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="rounded-full"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop fixed right, Mobile slide-in from right */}
      <div 
        className={`fixed top-0 right-0 h-screen w-72 sm:w-80 lg:w-64 bg-white border-l border-border/50 shadow-lg z-[60] flex flex-col transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'} lg:translate-x-0`} 
        data-testid="sidebar"
      >
      {/* Logo Section */}
      <div className="p-6 border-b border-border/50">
        {/* Mobile Close Button */}
        <div className="lg:hidden flex justify-end mb-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(false)}
            className="rounded-full -mr-2 -mt-2"
            data-testid="mobile-menu-close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        <Link to={homePath} className="flex flex-col items-center gap-3" data-testid="navbar-logo">
          {settings?.logo_url ? (
            <img 
              src={getFullUrl(settings.logo_url)} 
              alt={settings?.site_name || "ODR Academy"} 
              className="h-16 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className="w-20 h-20 rounded-full bg-primary items-center justify-center shadow-md"
            style={{ display: settings?.logo_url ? 'none' : 'flex' }}
          >
            <span className="text-white font-serif font-bold text-3xl">O</span>
          </div>
          <div className="text-center">
            <h1 className="font-serif text-lg font-semibold text-foreground leading-tight">
              {settings?.site_name || "ODR Academy"}
            </h1>
            <p className="text-xs text-muted-foreground">{settings?.site_subtitle || "Estetica Oncologica"}</p>
          </div>
        </Link>
      </div>

      {/* User Info */}
      <div className="px-4 py-3 border-b border-border/50 bg-secondary/30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        {isAdmin && (
          <span className="inline-block mt-2 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
            {user?.role === "admin" ? "Admin" : "Docente"}
          </span>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 overflow-y-auto p-4">
        {/* Operatore Fiera - Menu super limitato */}
        {isOperatoreFiera ? (
          <div className="mb-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-2">
              Menu
            </p>
            <div className="space-y-1">
              {operatoreFieraLinks.map((link) => (
                <Link key={link.path} to={link.path}>
                  <Button
                    variant={isActive(link.path) ? "default" : "ghost"}
                    className={`w-full justify-start rounded-xl ${
                      isActive(link.path) ? "" : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                  >
                    <link.icon className="w-4 h-4 mr-3" />
                    {link.label}
                  </Button>
                </Link>
              ))}
            </div>
          </div>
        ) : user?.role === "cliente_accademia" ? (
          /* CLIENTE ACCADEMIA ODR - solo Biblioteca + Scienza Benessere */
          <div className="mb-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-2">
              Accademia ODR
            </p>
            <div className="space-y-1">
              {clienteAccademiaLinks.map((link) => (
                <Link key={link.path} to={link.path}>
                  <Button
                    variant={isActive(link.path) ? "default" : "ghost"}
                    className={`w-full justify-start rounded-xl ${
                      isActive(link.path)
                        ? "bg-gradient-to-r from-[#7A4856] to-[#5A3340] text-white"
                        : "text-muted-foreground hover:text-foreground hover:bg-rose-50"
                    }`}
                    data-testid={`nav-link-accademia-${link.path.replace("/", "")}`}
                  >
                    <link.icon className="w-4 h-4 mr-3" />
                    {link.label}
                  </Button>
                </Link>
              ))}
            </div>
          </div>
        ) : user?.role === "potenziale_cliente" ? (
          /* POTENZIALE CLIENTE - SOLO SEZIONE BENESSERE */
          <div className="mb-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-2">
              Benessere
            </p>
            <div className="space-y-1">
              {potenzialeClienteLinks.map((link) => (
                <Link key={link.path} to={link.path}>
                  <Button
                    variant={isActive(link.path) ? "default" : "ghost"}
                    className={`w-full justify-start rounded-xl ${
                      isActive(link.path) 
                        ? "bg-gradient-to-r from-teal-500 to-cyan-500 text-white" 
                        : "text-muted-foreground hover:text-foreground hover:bg-teal-50"
                    }`}
                    data-testid={`nav-link-benessere-${link.path.replace("/benessere/", "") || "home"}`}
                  >
                    <link.icon className="w-4 h-4 mr-3" />
                    {link.label}
                  </Button>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <>
        {/* CLIENTI (estetisti/parrucchieri) e DISTRIBUTORI/AGENTI - VEDONO TUTTO */}
        <div className="mb-6">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-2">
            Menu
          </p>
          <div className="space-y-1">
            {userLinks.map((link) => (
              <Link key={link.path} to={link.path}>
                <Button
                  variant={isActive(link.path) ? "default" : "ghost"}
                  className={`w-full justify-start rounded-xl ${
                    isActive(link.path) ? "" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                >
                  <link.icon className="w-4 h-4 mr-3" />
                  {link.label}
                </Button>
              </Link>
            ))}
          </div>
        </div>

        {/* Sezione Benessere - Per clienti e distributori/agenti */}
        <div className="mb-6">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-2">
            Benessere
          </p>
          <div className="space-y-1">
            {publicLinks.map((link) => (
              <Link key={link.path} to={link.path}>
                <Button
                  variant={isActive(link.path) ? "default" : "ghost"}
                  className={`w-full justify-start rounded-xl ${
                    isActive(link.path) 
                      ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white" 
                      : "text-muted-foreground hover:text-foreground hover:bg-pink-50"
                  }`}
                  data-testid={`nav-link-benessere-${link.path.replace("/benessere/", "") || "home"}`}
                >
                  <link.icon className="w-4 h-4 mr-3" />
                  {link.label}
                </Button>
              </Link>
            ))}
          </div>
        </div>

        {/* Pulsante Prevenzione - sempre visibile per admin */}
        {isAdmin && (
          <div className="mb-3">
            <a href="/prevenzione" target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                className="w-full justify-start rounded-xl border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-400"
                data-testid="nav-link-prevenzione"
              >
                <Microscope className="w-4 h-4 mr-3" />
                Prevenzione
                <ExternalLink className="w-3 h-3 ml-auto opacity-60" />
              </Button>
            </a>
          </div>
        )}

        {/* Pulsante Protocollo Cabina ODR - tra Prevenzione e Blog WordPress */}
        {isAdmin && (
          <div className="mb-3 space-y-2">
            <Link to="/protocollo-cabina">
              <Button
                variant={isActive("/protocollo-cabina") ? "default" : "outline"}
                className={`w-full justify-start rounded-xl ${
                  isActive("/protocollo-cabina")
                    ? "bg-gradient-to-r from-teal-500 to-emerald-500 text-white"
                    : "border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100 hover:text-teal-800 dark:bg-teal-950/30 dark:border-teal-800 dark:text-teal-400"
                }`}
                data-testid="nav-link-protocollo-cabina"
              >
                <Sparkles className="w-4 h-4 mr-3" />
                Protocollo Cabina Estetiste
              </Button>
            </Link>
            <Link to="/protocollo-parrucchieri">
              <Button
                variant={isActive("/protocollo-parrucchieri") ? "default" : "outline"}
                className={`w-full justify-start rounded-xl ${
                  isActive("/protocollo-parrucchieri")
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                    : "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-400"
                }`}
                data-testid="nav-link-protocollo-parrucchieri"
              >
                <Sparkles className="w-4 h-4 mr-3" />
                Protocollo Salone Parrucchieri
              </Button>
            </Link>
          </div>
        )}

        {/* Pulsante Blog WordPress - sempre visibile per admin */}
        {isAdmin && (
          <div className="mb-6">
            <Link to="/admin/blog-wp">
              <Button
                variant={isActive("/admin/blog-wp") ? "default" : "outline"}
                className={`w-full justify-start rounded-xl ${
                  isActive("/admin/blog-wp")
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                    : "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-400"
                }`}
                data-testid="nav-link-blog-wp"
              >
                <Rss className="w-4 h-4 mr-3" />
                Blog WordPress
              </Button>
            </Link>
          </div>
        )}

        {/* Admin Links - Grouped */}
        {isAdmin && (
          <div>
            {/* Dashboard - sempre visibile */}
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-2">
              Amministrazione
            </p>
            <div className="space-y-0.5 mb-3">
              <Link to="/admin">
                <Button
                  variant={isActive("/admin") ? "default" : "ghost"}
                  className={`w-full justify-start rounded-xl ${
                    isActive("/admin") ? "" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="admin-link-dashboard"
                >
                  <Settings className="w-4 h-4 mr-3" />
                  Dashboard
                </Button>
              </Link>
            </div>

            {/* Grouped sections */}
            <div className="space-y-1">
              {adminGroups.map((group) => {
                const GroupIcon = group.icon;
                const isOpen = openGroups[group.id];
                const allGroupLinks = [
                  ...group.links,
                  ...(group.subGroups || []).flatMap((sg) => sg.links),
                ];
                const hasActiveChild = allGroupLinks.some((l) => isActive(l.path));

                return (
                  <div key={group.id}>
                    {/* Group header */}
                    <button
                      onClick={() => !group.comingSoon && toggleGroup(group.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm transition-colors ${
                        hasActiveChild
                          ? "bg-primary/10 text-primary font-medium"
                          : group.comingSoon
                          ? "text-muted-foreground/50 cursor-default"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent"
                      }`}
                      data-testid={`admin-group-${group.id}`}
                    >
                      <span className="flex items-center gap-2.5">
                        <GroupIcon className="w-4 h-4" />
                        <span>{group.label}</span>
                        {group.comingSoon && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 font-medium">
                            Soon
                          </span>
                        )}
                      </span>
                      {!group.comingSoon && (
                        isOpen
                          ? <ChevronDown className="w-3.5 h-3.5 opacity-50" />
                          : <ChevronRight className="w-3.5 h-3.5 opacity-50" />
                      )}
                    </button>

                    {/* Group links */}
                    {isOpen && !group.comingSoon && (
                      <div className="ml-3 mt-0.5 mb-1 space-y-0.5 border-l-2 border-primary/15 pl-3">
                        {group.links.map((link) => (
                          <Link key={link.path} to={link.path}>
                            <Button
                              variant={isActive(link.path) ? "default" : "ghost"}
                              size="sm"
                              className={`w-full justify-start rounded-lg text-[13px] h-8 ${
                                isActive(link.path) ? "" : "text-muted-foreground hover:text-foreground"
                              }`}
                              data-testid={`admin-link-${link.path.replace("/admin/", "").replace("/", "-")}`}
                            >
                              <link.icon className="w-3.5 h-3.5 mr-2" />
                              {link.label}
                            </Button>
                          </Link>
                        ))}

                        {/* Sub-groups (e.g., Materiale Didattico) */}
                        {(group.subGroups || []).map((sg) => {
                          const SubIcon = sg.icon;
                          const isSubOpen = openGroups[sg.id];
                          const hasActiveSub = sg.links.some((l) => isActive(l.path));
                          return (
                            <div key={sg.id}>
                              <button
                                onClick={() => toggleGroup(sg.id)}
                                className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[13px] transition-colors ${
                                  hasActiveSub
                                    ? "text-primary font-medium"
                                    : "text-muted-foreground hover:text-foreground"
                                }`}
                                data-testid={`admin-subgroup-${sg.id}`}
                              >
                                <span className="flex items-center gap-2">
                                  <SubIcon className="w-3.5 h-3.5" />
                                  {sg.label}
                                </span>
                                {isSubOpen ? <ChevronDown className="w-3 h-3 opacity-50" /> : <ChevronRight className="w-3 h-3 opacity-50" />}
                              </button>
                              {isSubOpen && (
                                <div className="ml-3 mt-0.5 space-y-0.5 border-l border-primary/10 pl-2">
                                  {sg.links.map((link) => (
                                    <Link key={link.path} to={link.path}>
                                      <Button
                                        variant={isActive(link.path) ? "default" : "ghost"}
                                        size="sm"
                                        className={`w-full justify-start rounded-lg text-xs h-7 ${
                                          isActive(link.path) ? "" : "text-muted-foreground hover:text-foreground"
                                        }`}
                                        data-testid={`admin-link-${link.path.replace("/admin/", "").replace("/", "-")}`}
                                      >
                                        <link.icon className="w-3 h-3 mr-2" />
                                        {link.label}
                                      </Button>
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
          </>
        )}
      </nav>

      {/* Logout Button */}
      <div className="p-4 border-t border-border/50 space-y-3">
        {/* Push Notifications & Install */}
        <PushNotificationManager onAction={() => setMobileMenuOpen(false)} />
        
        <Button
          variant="ghost"
          className="w-full justify-start rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={logout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-3" />
          Esci
        </Button>
      </div>
    </div>
    </>
  );
};

// Layout wrapper to add padding for the sidebar
export const MainLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {/* pt-16 for mobile header, lg:pt-0 for desktop; mr for sidebar on desktop only */}
      <div className="pt-16 lg:pt-0 lg:mr-64">
        {children}
      </div>
    </div>
  );
};
